import {
  MANUAL_TASK_TYPES,
  RECORD_ENTITY_LABELS,
  TASK_TYPE_LABELS,
  type ManualTaskType,
  type NoteSource,
  type RecordEntity,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, ExternalLink, ListTodo, NotebookPen, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type MemberView } from '../../lib/client.ts';
import { useMembers } from '../../lib/record-data.ts';
import { cn } from '../../lib/utils.ts';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Input, nativeSelectClass, Textarea } from '../ui/input.tsx';
import {
  blankTask,
  readDraft,
  writeDraft,
  type MeetingDraft,
  type Target,
  type TaskDraft,
} from './drafts.ts';
import { isInternal, type Meeting } from './meeting.ts';
import { dayOf, noteBody, timeOf } from './note-body.ts';
import { surfaceLabel, type Surface } from './surface.ts';

const label = 'text-muted-foreground text-xs font-bold tracking-[0.08em] uppercase';
const box = 'border-border flex flex-col gap-2 rounded-xl border p-3';
const fieldLabel = 'text-muted-foreground flex min-w-0 flex-col gap-0.5 text-xs';
/** The order a project's records are listed in, as the project page's "Add note" tree. */
const ORDER: RecordEntity[] = ['projects', 'companies', 'contacts', 'deals', 'jobs', 'candidates'];

/** "in 12 min", "live · 23 min", "ended 5 min ago": refreshed every 30 s. */
function useClock(meeting: Meeting): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const start = Date.parse(meeting.start);
  const end = Date.parse(meeting.end);
  const min = (ms: number) => Math.max(0, Math.round(ms / 60_000));
  if (now < start) return `starts in ${min(start - now)} min`;
  if (now <= end) return `live · ${min(now - start)} min`;
  return `ended ${min(now - end)} min ago`;
}

const keyOf = (t: Target) => `${t.entityType}:${t.entityId}`;

/** A section's outcome: what was saved where, or why not. */
function Outcome({ saved, error }: { saved: string | null; error: string | null }) {
  if (error)
    return (
      <p className="text-danger text-sm" role="alert">
        {error}
      </p>
    );
  if (saved)
    return (
      <p className="text-state-success-fg flex items-center gap-1.5 text-sm" role="status">
        <Check className="size-4 shrink-0" aria-hidden /> {saved}
      </p>
    );
  return null;
}

/** A task's assignee, due date and type, each labelled. */
function TaskFields({
  value,
  members,
  onChange,
}: {
  value: TaskDraft;
  members: MemberView[];
  onChange: (patch: Partial<TaskDraft>) => void;
}) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <label className={fieldLabel}>
        Assignee
        <select
          aria-label="Assignee"
          className={nativeSelectClass}
          value={value.assigneeId ?? ''}
          onChange={(e) => onChange({ assigneeId: e.target.value || null })}
        >
          <option value="">Me</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name || m.email}
            </option>
          ))}
        </select>
      </label>
      <label className={fieldLabel}>
        Due date
        <Input
          type="date"
          aria-label="Due date"
          value={value.due ?? ''}
          onChange={(e) => onChange({ due: e.target.value || null })}
        />
      </label>
      <label className={fieldLabel}>
        Type
        <select
          aria-label="Type"
          className={nativeSelectClass}
          value={value.type}
          onChange={(e) => onChange({ type: e.target.value as ManualTaskType })}
        >
          {MANUAL_TASK_TYPES.map((type) => (
            <option key={type} value={type}>
              {TASK_TYPE_LABELS[type]}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** A project's records as a native select, grouped by kind (no Radix: see the file header). */
function RecordSelect({
  id,
  name,
  sources,
  value,
  onChange,
}: {
  id: string;
  name: string;
  sources: NoteSource[];
  value: NoteSource | undefined;
  onChange: (t: Target) => void;
}) {
  return (
    <select
      id={id}
      aria-label={name}
      className={nativeSelectClass}
      value={value ? keyOf(value) : ''}
      onChange={(e) => {
        const [entityType, entityId] = e.target.value.split(':') as [RecordEntity, string];
        onChange({ entityType, entityId });
      }}
    >
      {ORDER.map((kind) => {
        const items = sources.filter((s) => s.entityType === kind);
        if (!items.length) return null;
        return (
          <optgroup key={kind} label={RECORD_ENTITY_LABELS[kind]}>
            {items.map((s) => (
              <option key={s.entityId} value={keyOf(s)}>
                {s.label}
              </option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );
}

/**
 * The Meeting Companion (TS-1963): what an account manager keeps open beside a client
 * call, in its own floating window (`meeting-companion-host.tsx`).
 *
 *  - The meeting and how long it has run, with Join.
 *  - The client's project, found from the attendees' emails (`POST /projects:lookup`) or picked
 *    by hand.
 *  - NOTE, saved by its own "Save note" to the record picked right under it: the project or any
 *    record under it (its company, contacts, deals, jobs, candidates, as the project page's "Add
 *    note" tree).
 *  - TASKS, a separate section: each saved by its own "Save task", straight away, on the PROJECT
 *    (the record page that shows tasks; contacts, deals and jobs do not), with an assignee, a due
 *    date and a type; the ones saved this meeting are listed under it.
 *
 * What is typed and not saved is a draft in this browser (`drafts.ts`). Plain elements only, no
 * Radix menus or dialogs: in a Picture-in-Picture window those portal to the main tab's body
 * (see `external-window-portal.tsx`).
 */
export function MeetingCompanion({
  meeting,
  surface,
  onClose,
}: {
  meeting: Meeting;
  surface: Surface;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const clock = useClock(meeting);
  const { members, nameOf } = useMembers();
  const [draft, setDraft] = useState<MeetingDraft>(() => readDraft(meeting.id));
  const [noteSaved, setNoteSaved] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  /** The tasks saved during this meeting, for the AM to see what went in. */
  const [savedTasks, setSavedTasks] = useState<Array<TaskDraft & { id: string }>>([]);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const firstWrite = useRef(true);

  // The draft is written as it changes (debounced), so a closed window loses nothing.
  useEffect(() => {
    if (firstWrite.current) {
      firstWrite.current = false;
      return;
    }
    const t = window.setTimeout(() => writeDraft(meeting.id, draft), 400);
    return () => window.clearTimeout(t);
  }, [draft, meeting.id]);
  const edit = (patch: Partial<MeetingDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const editTask = (patch: Partial<TaskDraft>) => {
    setTaskError(null);
    setDraft((d) => ({ ...d, task: { ...d.task, ...patch } }));
  };

  // The client's project: each external attendee's email through the project lookup.
  const emails = useMemo(
    () =>
      [...new Set(meeting.attendees.map((a) => a.email.toLowerCase()))].filter(
        (e) => !isInternal(e),
      ),
    [meeting.attendees],
  );
  const lookup = useQuery({
    queryKey: ['meeting-companion', 'lookup', emails],
    enabled: emails.length > 0 && !meeting.projectId,
    queryFn: async () => {
      const found = new Map<string, { id: string; name: string }>();
      for (const email of emails)
        for (const item of (await api.projects.lookup(email)).items)
          found.set(item.project.id, { id: item.project.id, name: item.project.name });
      return [...found.values()];
    },
  });
  const matches = lookup.data ?? [];
  const projectId =
    draft.projectId ?? meeting.projectId ?? (matches.length === 1 ? matches[0]!.id : null);
  const searchResults = useQuery({
    queryKey: ['meeting-companion', 'projects', search.trim()],
    enabled: searching && search.trim().length >= 2,
    queryFn: () => api.projects.list({ q: search.trim(), limit: 8 }),
  });
  const project = useQuery({
    queryKey: ['project', projectId],
    enabled: !!projectId,
    queryFn: () => api.projects.get(projectId!),
  });
  const pickProject = (id: string) => edit({ projectId: id, noteTarget: null });

  // Where the note can go: the project and everything under it. Tasks go on the project only.
  const sources = useQuery({
    queryKey: ['notes', 'sources', 'projects', projectId],
    enabled: !!projectId,
    queryFn: () => api.notes.sources('projects', projectId!),
  });
  const writable = (sources.data ?? []).filter((s) => s.canWrite);
  const projectSource = writable.find((s) => s.entityType === 'projects');
  const noteTo =
    writable.find((s) => draft.noteTarget && keyOf(s) === keyOf(draft.noteTarget)) ?? projectSource;
  const active = members.filter((m) => m.status === 'active');
  const refresh = (keys: string[][]) =>
    Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));

  const saveNote = useMutation({
    mutationFn: () =>
      api.notes.create({
        entityType: noteTo!.entityType,
        entityId: noteTo!.entityId,
        body: noteBody(meeting, draft),
        pinned: false,
      }),
    onSuccess: async () => {
      setNoteError(null);
      setNoteSaved(`Note saved to ${noteTo!.label}.`);
      edit({ notes: '' });
      await refresh([['notes'], ['activities']]);
    },
    onError: (err) => setNoteError(describeError(err)),
  });

  const saveTask = useMutation({
    mutationFn: (t: TaskDraft) =>
      api.tasks.create({
        entityType: 'projects',
        entityId: projectSource!.entityId,
        title: t.title.trim(),
        type: t.type,
        ...(t.assigneeId ? { assigneeId: t.assigneeId } : {}),
        // The end of the assignee's working day on the chosen date.
        ...(t.due ? { dueAt: new Date(`${t.due}T17:00:00`).toISOString() } : {}),
      }),
    onSuccess: async (created, t) => {
      setTaskError(null);
      setSavedTasks((list) => [...list, { ...t, title: t.title.trim(), id: created.id }]);
      // A fresh task, keeping the assignee: several tasks for one person are common in a call.
      edit({ task: { ...blankTask(), assigneeId: t.assigneeId } });
      await refresh([['tasks'], ['activities']]);
    },
    onError: (err) => setTaskError(describeError(err)),
  });

  return (
    <div className="flex h-full flex-col" data-testid="meeting-companion">
      {/* The meeting. */}
      <header className="border-border flex flex-col gap-2 border-b px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-semibold" title={meeting.title}>
              {meeting.title}
            </p>
            <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <CalendarClock className="size-3.5" aria-hidden />
              {dayOf(meeting.start)}, {timeOf(meeting.start)}–{timeOf(meeting.end)} · {clock}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Badge variant="outline">{surfaceLabel(surface)}</Badge>
            <Button size="sm" variant="ghost" aria-label="Close the companion" onClick={onClose}>
              <X />
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {meeting.joinUrl && (
            <Button size="sm" asChild>
              <a href={meeting.joinUrl} target="_blank" rel="noreferrer">
                <ExternalLink /> Join
              </a>
            </Button>
          )}
          {meeting.attendees.map((a) => (
            <span
              key={a.email}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs',
                isInternal(a.email) ? 'text-muted-foreground border-border' : 'border-brand/40',
              )}
              title={a.email}
            >
              {a.name ?? a.email}
            </span>
          ))}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
        {/* The client: the project the note and tasks belong under. */}
        <section className="flex flex-col gap-2" aria-label="Client">
          <p className={label}>Client</p>
          {lookup.isFetching && (
            <p className="text-muted-foreground text-sm">Finding the client…</p>
          )}
          {project.data && !searching && (
            <div className="flex items-center justify-between gap-2">
              <a
                // Absolute: inside a Picture-in-Picture window a relative link resolves
                // against its blank document, not the app.
                href={new URL(`/projects/${project.data.id}`, window.location.origin).href}
                target="_blank"
                rel="noreferrer"
                className="truncate text-sm font-medium hover:underline"
              >
                {project.data.name}
              </a>
              <Button size="sm" variant="ghost" onClick={() => setSearching(true)}>
                Change
              </Button>
            </div>
          )}
          {!projectId && !lookup.isFetching && matches.length > 1 && !searching && (
            <div className="flex flex-col gap-1">
              <p className="text-muted-foreground text-xs">
                The attendees are on several projects: which one is this meeting about?
              </p>
              {matches.map((m) => (
                <Button
                  key={m.id}
                  size="sm"
                  variant="outline"
                  className="justify-start"
                  onClick={() => pickProject(m.id)}
                >
                  {m.name}
                </Button>
              ))}
            </div>
          )}
          {((!projectId && !lookup.isFetching && matches.length === 0) || searching) && (
            <div className="flex flex-col gap-1.5">
              {!searching && (
                <p className="text-muted-foreground text-xs">
                  No project has these attendees. Search for the client:
                </p>
              )}
              <Input
                aria-label="Search projects"
                placeholder="Project name…"
                value={search}
                onChange={(e) => {
                  setSearching(true);
                  setSearch(e.target.value);
                }}
              />
              {searchResults.data?.items.map((p) => (
                <Button
                  key={p.id}
                  size="sm"
                  variant="ghost"
                  className="justify-start"
                  onClick={() => {
                    pickProject(p.id);
                    setSearching(false);
                    setSearch('');
                  }}
                >
                  {p.name}
                </Button>
              ))}
              {searching && projectId && (
                <Button size="sm" variant="ghost" onClick={() => setSearching(false)}>
                  Cancel
                </Button>
              )}
            </div>
          )}
        </section>

        {/* The note: where it goes, and its own save, right under it. */}
        <section className={cn(box, 'min-h-48 flex-1')} aria-label="Note">
          <p className={cn(label, 'flex items-center gap-1.5')}>
            <NotebookPen className="size-3.5" aria-hidden /> Note
          </p>
          <Textarea
            aria-label="Meeting notes"
            placeholder="What was said, decided, asked… Markdown: **bold**, lists, links"
            className="min-h-36 flex-1 resize-none"
            value={draft.notes}
            onChange={(e) => {
              setNoteSaved(null);
              setNoteError(null);
              edit({ notes: e.target.value });
            }}
          />
          {writable.length > 0 && (
            <div className="flex items-center gap-2">
              <label
                className="flex min-w-0 flex-1 items-center gap-2 text-sm"
                htmlFor="mc-note-to"
              >
                <span className="text-muted-foreground shrink-0">Save note to</span>
                <RecordSelect
                  id="mc-note-to"
                  name="Save note to"
                  sources={writable}
                  value={noteTo}
                  onChange={(t) => {
                    setNoteSaved(null);
                    edit({ noteTarget: t });
                  }}
                />
              </label>
              <Button
                className="shrink-0"
                onClick={() => saveNote.mutate()}
                disabled={!noteTo || !draft.notes.trim() || saveNote.isPending}
                loading={saveNote.isPending}
              >
                Save note
              </Button>
            </div>
          )}
          <Outcome saved={noteSaved} error={noteError} />
        </section>

        {/* Tasks: their own section, each saved by its own button, on the project. */}
        <section className={box} aria-label="Tasks">
          <p className={cn(label, 'flex items-center gap-1.5')}>
            <ListTodo className="size-3.5" aria-hidden /> Tasks
          </p>
          {projectSource && (
            <p className="text-muted-foreground text-xs">
              Created on the project <span className="text-foreground">{projectSource.label}</span>
            </p>
          )}
          <form
            className="flex flex-col gap-1.5"
            aria-label="New task"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.task.title.trim() && projectSource) saveTask.mutate(draft.task);
            }}
          >
            <Input
              aria-label="Task"
              placeholder="Send the shortlist by Friday…"
              value={draft.task.title}
              onChange={(e) => editTask({ title: e.target.value })}
            />
            <TaskFields value={draft.task} members={active} onChange={editTask} />
            <Button
              type="submit"
              className="self-end"
              disabled={!projectSource || !draft.task.title.trim() || saveTask.isPending}
              loading={saveTask.isPending}
            >
              Save task
            </Button>
          </form>
          <Outcome saved={null} error={taskError} />
          {savedTasks.length > 0 && (
            <ul className="flex flex-col gap-1" aria-label="Saved tasks">
              {savedTasks.map((t) => (
                <li
                  key={t.id}
                  className="border-border bg-muted/30 flex items-start gap-2 rounded-lg border px-2 py-1.5 text-sm"
                >
                  <Check className="text-state-success-fg mt-0.5 size-4 shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="block truncate">{t.title}</span>
                    <span className="text-muted-foreground text-xs">
                      {t.assigneeId ? nameOf(t.assigneeId) : 'Me'}
                      {t.due ? ` · due ${t.due}` : ''} · {TASK_TYPE_LABELS[t.type]}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
